import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Navbar } from './Navbar';
import { AttestationBlockModal } from '../AttestationBlockModal';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [attestationStatus, setAttestationStatus] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only check attestation for RH and commercial users
    if (user && (user.role === 'rh' || user.role === 'commercial')) {
      checkAttestationStatus();
    } else {
      setIsChecking(false);
    }
  }, [user]);

  // Re-check when location changes to prevent URL navigation bypass
  useEffect(() => {
    if (user && (user.role === 'rh' || user.role === 'commercial') && attestationStatus?.blocked) {
      // If blocked and trying to access any page other than attestations, show modal
      if (location.pathname !== '/attestations') {
        setShowBlockModal(true);
      }
    }
  }, [location.pathname, attestationStatus, user]);

  const checkAttestationStatus = async () => {
    setIsChecking(true);
    try {
      const response = await fetch(`${API}/attestations/status`, { credentials: 'include' });
      if (response.ok) {
        const status = await response.json();
        setAttestationStatus(status);
        
        // Show block modal if attestation is required, pending, or expired
        if (status.blocked) {
          setShowBlockModal(true);
          // Redirect to attestations page
          if (location.pathname !== '/attestations') {
            navigate('/attestations');
          }
        } else {
          setShowBlockModal(false);
        }
      }
    } catch (error) {
      console.error('Failed to check attestation status:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleAttestationSuccess = () => {
    // Re-check status after upload
    checkAttestationStatus();
  };

  // Show loading while checking attestation status
  if (isChecking && user && (user.role === 'rh' || user.role === 'commercial')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Verification en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-72">
        <Navbar onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Attestation Block Modal - Cannot be closed */}
      <AttestationBlockModal
        isOpen={showBlockModal}
        status={attestationStatus}
        onSuccess={handleAttestationSuccess}
      />
    </div>
  );
};
